import { useCallback } from 'react';
import { TaskCategory, getBackendTimestamp, useTaskCreate } from '@sib/api';
import { createUseAppTranslation } from '@sib/i18n';
import { ContactOption } from '../../types/contact';
import {
  OrganizationEmailFormValues,
  buildEmailTaskTags,
  buildMailtoLink,
  getEmailContactsList,
} from './organization-email-form.utils';

const useAppTranslation = createUseAppTranslation('organizations');

interface UseOrganizationEmailSubmitParams {
  organizationId?: string | null;
  selectedContacts: ContactOption[];
  onSuccess?: () => void;
  onTaskCreated?: () => void;
  onTaskError?: (error: Error) => void;
}

/**
 * Sends an organization email through the user's mail client and, when
 * requested, records it as a completed client email task.
 */
export const useOrganizationEmailSubmit = ({
  organizationId,
  selectedContacts,
  onSuccess,
  onTaskCreated,
  onTaskError,
}: UseOrganizationEmailSubmitParams) => {
  const { t } = useAppTranslation();
  const { mutateAsync: createTask } = useTaskCreate({
    onSuccess: onTaskCreated,
    onError: onTaskError,
  });

  const submit = useCallback(
    async ({ shouldCreateTask, ...email }: OrganizationEmailFormValues) => {
      if (shouldCreateTask) {
        const emails = [
          ...getEmailContactsList(email.recipient),
          ...getEmailContactsList(email.threadMembers),
        ];

        await createTask({
          category: TaskCategory.ClientEmail,
          title: t('components.organizationEmailForm.task.title', {
            emailSubject: email.emailTitle,
          }),
          description: t('components.organizationEmailForm.task.description', {
            emailBody: email.message,
          }),
          deadline: getBackendTimestamp(new Date()),
          organizationId: organizationId ?? null,
          completed: true,
          tags: buildEmailTaskTags(emails, selectedContacts),
        });
      }

      window.location.href = buildMailtoLink(email);
      onSuccess?.();
    },
    [createTask, onSuccess, organizationId, selectedContacts, t]
  );

  return { submit };
};
